export const surveyVersionStatuses = [
  'draft',
  'in_review',
  'approved',
  'published',
  'superseded',
  'retired',
] as const;

export type SurveyVersionStatus = (typeof surveyVersionStatuses)[number];

export type SurveyRecipientRole =
  | 'room_administrator'
  | 'survey_administrator'
  | 'moderator'
  | 'global_administrator';

export type SurveyRecipientCandidate = {
  memberId: string;
  displayName: string;
  picUrl?: string;
  roles: SurveyRecipientRole[];
};

export const surveyImmediateDangerPolicyIds = {
  noOp: 'immediate-danger-policy:no-op',
  sendEmail: 'immediate-danger-policy:send-email',
  sendEmailAndIntervention:
    'immediate-danger-policy:send-email-and-intervention',
} as const;

export const immediateDangerPolicyRequiresEmail = (policyId?: string) =>
  policyId === surveyImmediateDangerPolicyIds.sendEmail ||
  policyId === surveyImmediateDangerPolicyIds.sendEmailAndIntervention;

export type SurveyLanguageApprovalStatus =
  | 'draft'
  | 'in_review'
  | 'approved'
  | 'rejected';
export type SurveyContentOrigin =
  | 'source_authored'
  | 'generated_draft'
  | 'manually_authored';
export type SurveySourceReviewStatus = 'pending' | 'approved' | 'rejected';
export type SurveyPresentationVariantKind = 'standard' | 'simpler';
export type SurveyBlockType =
  | 'language_selection'
  | 'wording_selection'
  | 'route'
  | 'information'
  | 'consent'
  | 'section_break'
  | 'question'
  | 'completion';
export type SurveyResponseType =
  | 'free_text'
  | 'single_choice'
  | 'multiple_choice';
export type SurveyNonAnswerOutcome =
  | 'skip'
  | 'prefer_not_to_say'
  | 'do_not_know';

export type SurveyNonAnswerOption = {
  outcome: SurveyNonAnswerOutcome;
  labelContentId: string;
};
export type SurveyConsentClassification =
  | 'consent'
  | 'acknowledgement'
  | 'understanding'
  | 'preference';
export type SurveyConsentConsequenceAction =
  | 'continue'
  | 'pause'
  | 'end_survey'
  | 'block_start';
export type SurveyMessageKind =
  | 'technical_help'
  | 'inactivity_prompt'
  | 'consent_correction'
  | 'acknowledgement'
  | 'short_answer_check'
  | 'unclear_answer_check'
  | 'generated_help_unavailable'
  | 'recoverable_error';
export type SurveyControlAction =
  | 'continue'
  | 'pause'
  | 'resume'
  | 'end'
  | 'request_help'
  | 'use_simpler_wording'
  | 'use_standard_wording'
  | 'keep_answer'
  | 'change_answer';
export type SurveyDeliveryMode = 'non_generative' | 'generative_ai_help';
export type SurveyRepeatBehavior = 'repeat_exact' | 'repeat_with_approved_help';
export type SurveyContradictionBehavior =
  | 'accept_without_comment'
  | 'use_approved_prompt'
  | 'flag_for_review';
export type SurveyCompletionWrite =
  | 'source_response'
  | 'current_response'
  | 'survey_session'
  | 'survey_submission'
  | 'onboarding_progress'
  | 'notification';

export type SurveyVersionedReference = {
  id: string;
  version: string;
};

export type SurveyTranslationGeneration = {
  tool: string;
  toolVersion: string;
  generatedAt: string;
};

export type SurveyContentItem = {
  id: string;
  value: string;
  origin: SurveyContentOrigin;
  sourceContentId?: string;
  generation?: SurveyTranslationGeneration;
};

export type SurveyLanguageApproval = {
  status: SurveyLanguageApprovalStatus;
  reviewedByMemberId?: string;
  reviewedAt?: string;
};

export type SurveyLanguagePackage = {
  language: string;
  isSourceLanguage: boolean;
  content: Record<string, SurveyContentItem>;
  approval: SurveyLanguageApproval;
  contentHash?: string;
};

export type SurveySourceIntegrityReview = {
  status: SurveySourceReviewStatus;
  sourceReference: string;
  reviewedByMemberId?: string;
  reviewedAt?: string;
};

export type SurveyGovernanceMetadata = {
  governingRoomId: string;
  ownerMemberId: string;
  organizationId: string;
  responsibleTeam: string;
  governanceTeamMemberIds?: string[];
};

export type SurveyPurpose = {
  goal: string;
  audience: string[] | null;
  intendedUse: string | null;
  exclusions: string[] | null;
  estimatedMinutes: number;
  responseRecipientProfiles: SurveyVersionedReference[];
  responseRecipientMemberIds?: string[];
  nextSteps: string;
  downstreamEffect: string | null;
};

export type SurveyPathway = {
  id: string;
  name: string;
  audience: string;
  firstBlockId: string;
  sourceReview: SurveySourceIntegrityReview;
};

export type SurveyPresentationVariant = {
  id: string;
  name: string;
  kind: SurveyPresentationVariantKind;
  appliesToAllPathways: boolean;
  pathwayIds: string[];
  sourceReview: SurveySourceIntegrityReview;
};

export type SurveyRouteRule = {
  id: string;
  version: string;
  routeBlockId: string;
  optionId: string;
  destinationPathwayId: string;
  destinationProfileVersion: string;
  sourceReview: SurveySourceIntegrityReview;
};

export type SurveyBlockPresentation = {
  pathwayId?: string | null;
  presentationVariantId?: string | null;
  contentId: string;
  helpContentId?: string;
  sourceReview: SurveySourceIntegrityReview;
};

export type SurveyChoicePresentation = {
  pathwayId?: string | null;
  presentationVariantId?: string | null;
  contentId: string;
  sourceReview: SurveySourceIntegrityReview;
};

export type SurveyChoice = {
  id: string;
  canonicalValue: string;
  requiresFreeText?: boolean;
  appliesToAllPathways: boolean;
  pathwayIds: string[];
  presentations: SurveyChoicePresentation[];
  sourceReview: SurveySourceIntegrityReview;
};

export type SurveyQuestionConfiguration = {
  canonicalConceptId: string;
  responseType: SurveyResponseType;
  required: boolean;
  allowedNonAnswers: SurveyNonAnswerOption[];
  choices: SurveyChoice[];
  sourceReview: SurveySourceIntegrityReview;
};

export type SurveyWordingSelectionConfiguration = {
  simplerChoiceId: string;
  standardChoiceId: string;
};

export type SurveyConsentConsequence = {
  action: SurveyConsentConsequenceAction;
  contentId: string;
};

export type SurveyConsentConfiguration = {
  itemId: string;
  version: string;
  classification: SurveyConsentClassification;
  required: boolean;
  explanationContentId: string;
  acceptedLabelContentId: string;
  declinedLabelContentId: string;
  noConsequence: SurveyConsentConsequence;
  unansweredConsequence: SurveyConsentConsequence;
  amendmentsAllowed: boolean;
};

export type SurveyBlock = {
  id: string;
  order: number;
  type: SurveyBlockType;
  appliesToAllPathways: boolean;
  pathwayIds: string[];
  presentations: SurveyBlockPresentation[];
  question?: SurveyQuestionConfiguration;
  wordingSelection?: SurveyWordingSelectionConfiguration;
  consent?: SurveyConsentConfiguration;
};

export type SurveyMessageReference = {
  kind: SurveyMessageKind;
  contentId: string;
};

export type SurveyControlDefinition = {
  id: string;
  action: SurveyControlAction;
  labelContentId: string;
  blockTypes: SurveyBlockType[];
  deliveryModes: SurveyDeliveryMode[];
};

export type SurveyMessageBundle = {
  id: string;
  version: string;
  messages: SurveyMessageReference[];
  controls: SurveyControlDefinition[];
  inactivityTimeoutSeconds: number;
};

export type SurveySafetyConfiguration = {
  detectionEnabled: boolean;
  profile: SurveyVersionedReference;
  level: string;
  responsePolicy: SurveyVersionedReference;
  immediateDangerPolicy: SurveyVersionedReference;
  reportRecipientProfile: SurveyVersionedReference;
  safeguardRecipientMemberIds?: string[];
  fixedClinicalCorrectionContentId: string;
};

export type SurveyQuestionBehavior = {
  approvedHelpContentId: string;
  repeatBehavior: SurveyRepeatBehavior;
  generatedClarificationEnabled: boolean;
  answerQualityCheckEnabled: boolean;
  standardToSimplerEnabled: boolean;
  standardPresentationVariantId?: string;
  simplerPresentationVariantId?: string;
  shortAnswerCheckEnabled: boolean;
  minimumWordCount?: number;
  unclearAnswerCheckEnabled: boolean;
  clarificationTurnLimit: number;
  confirmationRequired: boolean;
  amendmentsAllowed: boolean;
  acknowledgementContentId: string;
  contradictionBehavior: SurveyContradictionBehavior;
};

export type SurveyQuestionBehaviorOverride = Partial<SurveyQuestionBehavior>;

export type SurveyAIConfiguration = {
  deliveryMode: SurveyDeliveryMode;
  generativeClarificationEnabled: boolean;
  answerQualityCheckEnabled: boolean;
  answeringSupportAssessmentEnabled: boolean;
  standardToSimplerEnabled: boolean;
  safety: SurveySafetyConfiguration;
  defaults: SurveyQuestionBehavior;
  overrides: Record<string, SurveyQuestionBehaviorOverride>;
  liveAIProfile?: SurveyVersionedReference;
  liveCapabilities: Array<{
    capability: string;
    outputSchema: SurveyVersionedReference;
    fixedFallbackContentId: string;
  }>;
};

export type SurveyCompletionConfiguration = {
  blockId: string;
  nextStepsContentId: string;
  recoverableErrorContentId: string;
  requiredWrites: SurveyCompletionWrite[];
  responseRecipientProfiles: SurveyVersionedReference[];
};

export type SurveyCodebookReference = {
  codebookId: string;
  codebookVersion: string;
  contentHash: string;
};

export type SurveyBuilderLiveThemeFailureBehavior =
  | 'allow_completion'
  | 'block_completion';

export type SurveyBuilderLiveThemeReviewPolicy =
  | 'not_required'
  | 'required';

export type SurveyBuilderLiveThemeAnalysisRequest = {
  codebook: SurveyCodebookReference;
  confidenceThreshold: number;
  maxThemesPerAnswer: number;
  maxInterpretationLength: number;
  failureBehavior: SurveyBuilderLiveThemeFailureBehavior;
  reviewPolicy: SurveyBuilderLiveThemeReviewPolicy;
};

export type SurveyLiveThemeAnalysis = SurveyBuilderLiveThemeAnalysisRequest & {
  profile?: unknown;
  outputSchema?: unknown;
  unclassifiedBehavior?: unknown;
};

export type SurveyVersionDefinition = {
  governance: SurveyGovernanceMetadata;
  purpose: SurveyPurpose;
  sourceLanguage: string;
  languages: SurveyLanguagePackage[];
  pathways: SurveyPathway[];
  routes: SurveyRouteRule[];
  presentationVariants: SurveyPresentationVariant[];
  blocks: SurveyBlock[];
  messageBundle: SurveyMessageBundle;
  aiConfiguration: SurveyAIConfiguration;
  liveThemeAnalysis?: SurveyLiveThemeAnalysis;
  aiArtifacts?: unknown;
  completion: SurveyCompletionConfiguration;
};

export type SurveyApproval = {
  approvedByMemberId: string;
  approvedAt: string;
  definitionHash: string;
  approvalVersion: string;
};

export type SurveyVersion = {
  id: string;
  surveyId: string;
  roomId: string;
  version: string;
  status: SurveyVersionStatus;
  definition: SurveyVersionDefinition;
  approval?: SurveyApproval;
  contentHash?: string;
  createdByMemberId: string;
  createdAt: string;
  updatedByMemberId: string;
  updatedAt: string;
  publishedAt?: string;
  concurrencyToken?: string;
};

export type SurveyCodebook = {
  id: string;
  itemType: 'survey_codebook';
  roomId: string;
  title: string;
  currentPublishedVersion?: string | null;
  createdByMemberId: string;
  createdAt: string;
  updatedByMemberId: string;
  updatedAt: string;
  _etag?: string | null;
};

export type SurveyCodebookCreateRequest = {
  title: string;
};

export type SurveyCodebookDefinition = {
  title: string;
  purpose: string;
  scope: string;
  ownerMemberId: string;
  organizationId: string;
  responsibleTeam: string;
  defaultConfidenceThreshold: number;
  maxThemesPerAnswer: number;
  maxInterpretationLength: number;
  evidenceRequired: true;
  entries: SurveyCodebookEntry[];
};

export type SurveyCodebookEntry = {
  code: string;
  label: string;
  definition: string;
  inclusionCriteria: string[];
  exclusionCriteria: string[];
  parentCode?: string | null;
  examples: string[];
  counterexamples: string[];
  active: boolean;
  generatedThemePromotion?: SurveyGeneratedThemePromotionProvenance | null;
};

export type SurveyGeneratedThemePromotionProvenance = {
  sourceSurveyId: string;
  sourceSurveyVersion: string;
  sourceCodebook: SurveyCodebookReference;
  candidateIds: string[];
  respondentCount: number;
  occurrenceCount: number;
  promotedByMemberId: string;
  promotedAtUnixMs: number;
};

export type SurveyGeneratedThemeCandidateReview = {
  surveyId: string;
  surveyVersion: string;
  codebook: SurveyCodebookReference;
  minimumRespondents: number;
  sessionsScanned: number;
  candidates: SurveyGeneratedThemeCandidate[];
};

export type SurveyGeneratedThemeCandidate = {
  candidateId: string;
  label: string;
  definition: string;
  respondentCount: number;
  occurrenceCount: number;
  questionBlockIds: string[];
};

export type SurveyGeneratedThemeEntryDraft = {
  code: string;
  label: string;
  definition: string;
  inclusionCriteria: string[];
  exclusionCriteria: string[];
  parentCode?: string | null;
};

export type SurveyGeneratedThemePromotionGroup = {
  candidateIds: string[];
  entry: SurveyGeneratedThemeEntryDraft;
};

export type SurveyGeneratedThemePromotionRequest = {
  surveyId: string;
  surveyVersion: string;
  targetVersion: string;
  groups: SurveyGeneratedThemePromotionGroup[];
};

export type SurveyCodebookApproval = {
  approvedByMemberId: string;
  approvedAt: string;
  definitionHash: string;
  approvalVersion: string;
};

export type SurveyCodebookVersion = {
  id: string;
  itemType: 'survey_codebook_version';
  codebookId: string;
  roomId: string;
  version: string;
  status: SurveyVersionStatus;
  definition: SurveyCodebookDefinition;
  approval?: SurveyCodebookApproval | null;
  contentHash?: string | null;
  createdByMemberId: string;
  createdAt: string;
  updatedByMemberId: string;
  updatedAt: string;
  publishedAt?: string | null;
  _etag?: string | null;
};

export type SurveyCodebookVersionDraftCreate = {
  codebookId: string;
  roomId: string;
  version: string;
  definition: SurveyCodebookDefinition;
};

export type SurveyCodebookVersionCopyCommand = {
  sourceVersion: string;
  version: string;
};

export type SurveyCodebookVersionDraftUpdate =
  SurveyCodebookVersionDraftCreate & {
    concurrencyToken: string;
  };

export type SurveyCodebookConcurrencyCommand = {
  concurrencyToken: string;
};

export type SurveyCodebookApprovalCommand = SurveyCodebookConcurrencyCommand & {
  approvalVersion: string;
};

export type SurveyVersionDraft = {
  surveyId: string;
  roomId: string;
  version: string;
  definition: SurveyVersionDefinition;
  concurrencyToken?: string;
};

export type SurveyGovernanceSummary = {
  isGoverned: boolean;
  currentPublishedVersion?: string;
  versions: SurveyVersionSummary[];
};

export type SurveyVersionSummary = {
  version: string;
  status: SurveyVersionStatus;
  publishedAt?: string;
};

export type SurveyBuilderLifecycleCapability = {
  status: SurveyVersionStatus;
  editable: boolean;
  allowedTransitions: SurveyVersionStatus[];
};

export type SurveyBuilderStructureRequirements = {
  minimumBlocks: number;
  minimumInformationBlocks: number;
  minimumConsentBlocks: number;
  minimumQuestionBlocks: number;
  requiredCompletionBlocks: number;
  minimumPathways: number;
  minimumStandardVariantsPerPathway: number;
  multiplePathwaysRequireRoute: boolean;
};

export type SurveyBuilderLimits = {
  maxSerializedBytes: number;
  maxObjectDepth: number;
  maxStringLength: number;
  maxCollectionItems: number;
  maxPathways: number;
  maxLanguages: number;
  maxPresentationVariants: number;
  maxBlocks: number;
  maxChoicesPerQuestion: number;
  maxRoutes: number;
  maxContentItemsPerLanguage: number;
  maxMessages: number;
  maxControls: number;
  maxVersionLength: number;
  maxCatalogSearchLength: number;
  minimumClarificationTurns: number;
  maximumClarificationTurns: number;
  defaultMinimumAnswerWords: number;
  minimumAnswerWords: number;
  maximumAnswerWords: number;
};

export type SurveyBuilderConfigurationOption = {
  value: string;
  displayName: string;
  description: string;
  selectable?: boolean;
  unavailableReason?: string | null;
};

export type SurveyBuilderConfigurationOptionGroup = {
  key: string;
  displayName?: string;
  description?: string;
  scope?: string;
  options: SurveyBuilderConfigurationOption[];
};

export type SurveyBuilderCapabilities = {
  contractVersion: string;
  supportedLanguages: string[];
  catalogTypes: string[];
  blockTypes: SurveyBlockType[];
  responseTypes: SurveyResponseType[];
  nonAnswerOutcomes: SurveyNonAnswerOutcome[];
  presentationVariantKinds: SurveyPresentationVariantKind[];
  deliveryModes: SurveyDeliveryMode[];
  configurationOptions?: SurveyBuilderConfigurationOptionGroup[];
  consentClassifications: SurveyConsentClassification[];
  consentConsequenceActions: SurveyConsentConsequenceAction[];
  completionWrites: SurveyCompletionWrite[];
  messageKinds: SurveyMessageKind[];
  controlActions: SurveyControlAction[];
  repeatBehaviors: SurveyRepeatBehavior[];
  contradictionBehaviors: SurveyContradictionBehavior[];
  languageApprovalStatuses: SurveyLanguageApprovalStatus[];
  sourceReviewStatuses: SurveySourceReviewStatus[];
  contentOrigins: SurveyContentOrigin[];
  versionStatuses: SurveyVersionStatus[];
  lifecycle: SurveyBuilderLifecycleCapability[];
  structure: SurveyBuilderStructureRequirements;
  limits: SurveyBuilderLimits;
};

export type SurveyBuilderTemplateSummary = {
  id: string;
  version: string;
  name: string;
  description: string;
  supportedSourceLanguages: string[];
  presentationVariantKinds: SurveyPresentationVariantKind[];
  deliveryMode: SurveyDeliveryMode;
  supportedDeliveryModes?: SurveyDeliveryMode[];
  requiresSourceReview: boolean;
  requiresLanguageApproval: boolean;
};

export type SurveyBuilderTemplateCreateRequest = {
  templateId: string;
  templateVersion: string;
  version: string;
  sourceLanguage: string;
  deliveryMode: SurveyDeliveryMode;
  liveThemeAnalysis: SurveyBuilderLiveThemeAnalysisRequest;
};

export const surveyBuilderCatalogTypes = {
  canonicalConcepts: 'canonical-concepts',
  organizations: 'organizations',
  responsibleTeams: 'responsible-teams',
  responseRecipientProfiles: 'response-recipient-profiles',
  safetyProfiles: 'safety-profiles',
  responsePolicies: 'response-policies',
  immediateDangerPolicies: 'immediate-danger-policies',
  reportRecipientProfiles: 'report-recipient-profiles',
  routeDestinationProfiles: 'route-destination-profiles',
  messageControlBundles: 'message-control-bundles',
  completionProfiles: 'completion-profiles',
} as const;

export type SurveyBuilderCatalogType =
  (typeof surveyBuilderCatalogTypes)[keyof typeof surveyBuilderCatalogTypes];

export type SurveyBuilderCatalogItem = {
  id: string;
  version: string;
  value: string;
  displayName: string;
  description: string;
  status: string;
};

export type SurveyBuilderCatalog = {
  catalogType: string;
  items: SurveyBuilderCatalogItem[];
};

export type SurveyValidationError = {
  code: string;
  path: string;
  message: string;
};

export type SurveyValidationResult = {
  errors: SurveyValidationError[];
};

export type SurveyConfigurationSelection = {
  pathwayId: string;
  presentationVariantId: string;
  language: string;
  deliveryMode: SurveyDeliveryMode;
};

export type SurveyResolutionError = {
  code: string;
  path: string;
  message: string;
};

export type SurveyEffectiveChoice = {
  id: string;
  canonicalValue: string;
  content: string;
};

export type GovernedSurveyEffectiveNonAnswerOption = {
  outcome: SurveyNonAnswerOutcome;
  label: string;
};

export type SurveyEffectiveQuestion = {
  canonicalConceptId: string;
  responseType: SurveyResponseType;
  required: boolean;
  allowedNonAnswers: GovernedSurveyEffectiveNonAnswerOption[];
  choices: SurveyEffectiveChoice[];
  behavior: SurveyQuestionBehavior;
};

export type SurveyEffectiveConsentConsequence = {
  action: SurveyConsentConsequenceAction;
  content: string;
};

export type SurveyEffectiveConsent = {
  itemId: string;
  version: string;
  classification: SurveyConsentClassification;
  required: boolean;
  explanation: string;
  noConsequence: SurveyEffectiveConsentConsequence;
  unansweredConsequence: SurveyEffectiveConsentConsequence;
  amendmentsAllowed: boolean;
};

export type SurveyEffectiveBlock = {
  id: string;
  order: number;
  type: SurveyBlockType;
  content: string;
  helpContent?: string;
  question?: SurveyEffectiveQuestion;
  consent?: SurveyEffectiveConsent;
};

export type SurveyEffectiveMessage = {
  kind: SurveyMessageKind;
  content: string;
};

export type SurveyEffectiveControl = {
  id: string;
  action: SurveyControlAction;
  label: string;
  blockTypes: SurveyBlockType[];
};

export type SurveyEffectiveSafetyConfiguration = Omit<
  SurveySafetyConfiguration,
  'detectionEnabled' | 'fixedClinicalCorrectionContentId'
> & {
  fixedClinicalCorrection: string;
};

export type SurveyEffectiveCompletionConfiguration = {
  blockId: string;
  completionContent: string;
  nextStepsContent: string;
  recoverableErrorContent: string;
  requiredWrites: SurveyCompletionWrite[];
  responseRecipientProfiles: SurveyVersionedReference[];
};

export type SurveyEffectiveConfiguration = {
  surveyId: string;
  version: string;
  selection: SurveyConfigurationSelection;
  definitionContentHash?: string;
  languageContentHash?: string;
  blocks: SurveyEffectiveBlock[];
  messages: SurveyEffectiveMessage[];
  controls: SurveyEffectiveControl[];
  safety: SurveyEffectiveSafetyConfiguration;
  completion: SurveyEffectiveCompletionConfiguration;
};

export type SurveyResolutionResult = {
  configuration?: SurveyEffectiveConfiguration;
  errors: SurveyResolutionError[];
};

export type SurveyMemberWordingMode = 'standard' | 'simpler';

export type SurveyMemberLanguageOption = {
  language: string;
  isSelected: boolean;
};

export type SurveyMemberPathwayOption = {
  id: string;
  name: string;
  audience: string;
  isSelected: boolean;
};

export type SurveyMemberWordingOption = {
  mode: SurveyMemberWordingMode;
  presentationVariantId: string;
  name: string;
  isSelected: boolean;
};

export type GovernedSurveyMemberExperience = {
  surveyId: string;
  roomId: string;
  title: string;
  description: string;
  version: string;
  languages: SurveyMemberLanguageOption[];
  selectedLanguage: string;
  pathways: SurveyMemberPathwayOption[];
  selectedPathwayId?: string | null;
  wordingOptions: SurveyMemberWordingOption[];
  selectedWordingMode?: SurveyMemberWordingMode | null;
  configuration?: SurveyEffectiveConfiguration | null;
  memberHasSubmitted: boolean;
};

export type GovernedSurveyExperienceSelection = {
  language?: string;
  pathwayId?: string;
  wording?: SurveyMemberWordingMode;
};

export type GovernedSurveySubmissionContext = {
  version: string;
  language: string;
  pathwayId: string;
  presentationVariantId: string;
  wordingMode: SurveyMemberWordingMode;
};

export type GovernedSurveyQuestionResponse = {
  blockId: string;
  exactText?: string;
  selectedChoiceIds: string[];
  nonAnswerOutcome?: SurveyNonAnswerOutcome;
};

export type GovernedSurveyConsentResponse = {
  blockId: string;
  accepted: boolean;
};

export type GovernedSurveySubmissionRequest = {
  context: GovernedSurveySubmissionContext;
  responses: GovernedSurveyQuestionResponse[];
  consents: GovernedSurveyConsentResponse[];
};

export type GovernedSurveySubmissionReceipt = {
  id: string;
  surveyId: string;
  surveyVersion: string;
  submittedAtUnixMs: number;
};

export type PublishedGovernedSurveyCatalogItem = {
  surveyId: string;
  title: string;
  description?: string | null;
  version: string;
  status: 'published';
  publishedAt: string;
  deliveryMode: SurveyDeliveryMode;
  contentHash: string;
  sourceLanguage: string;
  languages: string[];
};

export type EnhancedSurveyAnalysisSessionFilter =
  | 'all'
  | 'ongoing'
  | 'finished';

export type EnhancedSurveyRuntimeStatus =
  | 'in_progress'
  | 'paused'
  | 'skipped'
  | 'completed'
  | 'ended';

export type EnhancedSurveyAnalysisSessionSummary = {
  sessionId: string;
  memberId?: string | null;
  memberDisplayName?: string | null;
  surveyVersion: string;
  status: EnhancedSurveyRuntimeStatus;
  wordingMode: SurveyMemberWordingMode;
  startedAtUnixMs: number;
  updatedAtUnixMs: number;
  completedAtUnixMs?: number | null;
  durationMs: number;
  answerCount: number;
  helpCount: number;
  hasPendingAnalysis: boolean;
  hasFailedAnalysis: boolean;
};

export type EnhancedSurveyAnalysisSessionPage = {
  items: EnhancedSurveyAnalysisSessionSummary[];
  continuationToken?: string | null;
};

export type EnhancedSurveyAnalysisSelectedChoice = {
  choiceId: string;
  canonicalValue: string;
  standardLabel: string;
  simplerLabel?: string | null;
};

export type EnhancedSurveyAnalysisTimingEvent = {
  sequence: number;
  kind: string;
  occurredAtUnixMs: number;
  sourceEventId: string;
  wordingMode?: SurveyMemberWordingMode | null;
  presentationVariantId?: string | null;
  pauseReason?: string | null;
};

export type EnhancedSurveyAnalysisTiming = {
  presentedAtUnixMs: number;
  responseReceivedAtUnixMs: number;
  answerAcceptedAtUnixMs: number;
  questionCompletedAtUnixMs: number;
  elapsedResponseMs: number;
  wordingMode: SurveyMemberWordingMode;
  presentationVariantId: string;
  events: EnhancedSurveyAnalysisTimingEvent[];
};

export type EnhancedSurveyAnalysisHelpMessage = {
  sequence: number;
  role: 'assistant' | 'member';
  messageKind: string;
  conversationKind?: 'help' | 'clarification' | null;
  text: string;
  createdAtUnixMs: number;
  sourceEventId: string;
  wordingMode?: SurveyMemberWordingMode | null;
  presentationVariantId?: string | null;
};

export type EnhancedSurveyAnalysisTheme = {
  taxonomyCode: string;
  taxonomyLabel: string;
  taxonomyDefinition?: string | null;
  origin: 'codebook' | 'generated';
  interpretation: string;
  evidenceText: string;
  evidenceStartOffset: number;
  evidenceEndOffset: number;
  confidence: number;
  appliedConfidenceThreshold: number;
  reviewStatus: 'not_required' | 'pending' | 'approved' | 'rejected';
};

export type EnhancedSurveyAnalysisThemeResult = {
  status: 'classified' | 'unclassified' | 'failed';
  themes: EnhancedSurveyAnalysisTheme[];
  appliedConfidenceThreshold: number;
  failureCode?: string | null;
  failureDetail?: string | null;
  completedAtUnixMs: number;
};

export type EnhancedSurveyAnalysisAnswer = {
  blockId: string;
  order: number;
  questionNumber: number;
  standardWording: string;
  simplerWording?: string | null;
  responseType: SurveyResponseType;
  rawResponseText?: string | null;
  selectedChoices: EnhancedSurveyAnalysisSelectedChoice[];
  nonAnswerOutcome?: SurveyNonAnswerOutcome | null;
  acceptedAtUnixMs: number;
  wordingMode?: SurveyMemberWordingMode | null;
  timing?: EnhancedSurveyAnalysisTiming | null;
  helpTimeline: EnhancedSurveyAnalysisHelpMessage[];
  clarificationTimeline: EnhancedSurveyAnalysisHelpMessage[];
  liveThemes: EnhancedSurveyAnalysisThemeResult[];
  hasPendingAnalysis: boolean;
  hasFailedAnalysis: boolean;
};

export type EnhancedSurveyAnalysisConsent = {
  blockId: string;
  order: number;
  standardWording: string;
  simplerWording?: string | null;
  accepted: boolean;
  acceptedAtUnixMs: number;
};

export type EnhancedSurveyAnalysisTimelineEvent = {
  sequence: number;
  eventType: string;
  blockId?: string | null;
  rawResponseText?: string | null;
  selectedChoiceIds: string[];
  nonAnswerOutcome?: SurveyNonAnswerOutcome | null;
  interactionText?: string | null;
  wordingMode?: SurveyMemberWordingMode | null;
  createdAtUnixMs: number;
};

export type EnhancedSurveyAnalysisSessionDetail = {
  summary: EnhancedSurveyAnalysisSessionSummary;
  language: string;
  pathwayId?: string | null;
  answers: EnhancedSurveyAnalysisAnswer[];
  consents: EnhancedSurveyAnalysisConsent[];
  timeline: EnhancedSurveyAnalysisTimelineEvent[];
};

export type EnhancedSurveyAnalysisSessionQuery = {
  surveyVersion?: string;
  status?: EnhancedSurveyAnalysisSessionFilter;
  continuationToken?: string;
  memberSearch?: string;
};

export type EnhancedSurveyAnalysisExportQuery = Pick<
  EnhancedSurveyAnalysisSessionQuery,
  'surveyVersion' | 'status'
>;

export type EnhancedSurveyAnalysisWorkbookExport = {
  fileName: string;
  contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  respondentCount: number;
  contentBase64: string;
};

export type EnhancedSurveyAdminRetakeProgress = {
  stepId: string;
  status: string;
  collectedFields: string[];
  completedAt?: string | null;
  skipCount: number;
  nextPromptAt?: string | null;
  enhancedSurveySessionId?: string | null;
  enhancedSurveySkippedChatSessionId?: string | null;
};

export type EnhancedSurveyAdminRetakePreview = {
  roomId: string;
  memberId: string;
  memberDisplayName?: string | null;
  surveyId: string;
  surveyVersion: string;
  stepId: string;
  sessionId: string;
  runtimeStatus: EnhancedSurveyRuntimeStatus;
  answerCount: number;
  onboardingProgress?: EnhancedSurveyAdminRetakeProgress | null;
};

export type EnhancedSurveyAdminRetakeResult = {
  sessionId: string;
  removedOnboardingProgress: boolean;
  deletedDocumentCount: number;
  removedRecoveryEventCount: number;
};

export type AuthoringGovernedSurveyCatalogItem = {
  surveyId: string;
  title: string;
  description?: string | null;
  status?: string;
  dateStart?: string;
  dateEnd?: string | null;
  currentPublishedVersion?: string | null;
  versions: SurveyVersionSummary[];
};

export type GovernedSurveyDeletionRequest = {
  deleteRuntimeData: boolean;
};

export type GovernedSurveyDeletionResult = {
  surveyId: string;
  runtimeDataDeleted: boolean;
  removedOnboardingStepCount: number;
  deletedVersionDocumentCount: number;
  deletedRuntimeDocumentCount: number;
  retainedRuntimeResultDocumentCount: number;
  removedRecoveryEventCount: number;
  deletedSubmissionCount: number;
};

export type RoomOnboardingStep = {
  stepId: string;
  displayName?: string | null;
  order: number;
  promptCategory?: string | null;
  surveyId?: string | null;
  surveyVersion?: string | null;
  surveySubmissionNotificationEmails?: string[] | null;
  requiredFields: string[];
  optionalFields?: string[];
  completionMessage?: string | null;
  welcomeMessage?: string | null;
  isOptional: boolean;
  maxQuestionsPerNeed?: number | null;
};

export type RoomOnboardingConfiguration = {
  settingsRoomId?: string;
  isInherited?: boolean;
  supportsRoomOverrides?: boolean;
  supportsRoomScopedDisplayNames?: boolean;
  canInheritHubSettings?: boolean;
  supportsOptionalProfileFields?: boolean;
  supportsProfileCompletionMessage?: boolean;
  supportsProfileWelcomeMessage?: boolean;
  /** Placeholder names (without braces) accepted in the Profile completion message, e.g. `firstName`. */
  supportedCompletionMessagePlaceholders?: string[];
  roomId: string;
  agent?: string | null;
  onboardingSteps: RoomOnboardingStep[];
  concurrencyToken: string;
  supportedProfileFields?: string[];
  supportedNeedCategories?: string[];
};

export type RoomOnboardingConfigurationUpdate = {
  useHubSettings?: boolean;
  onboardingSteps: RoomOnboardingStep[];
  concurrencyToken: string;
};

export type SurveySourceReviewTargetType =
  | 'pathway'
  | 'presentation_variant'
  | 'block_presentation'
  | 'question'
  | 'choice'
  | 'choice_presentation'
  | 'route';
export type SurveySourceReviewDecision = 'approved' | 'rejected';

export type SurveySourceReviewCommand = {
  concurrencyToken: string;
  path: string;
  status: SurveySourceReviewDecision;
};

export type SurveyReviewBatchCommand = {
  concurrencyToken: string;
  status: SurveySourceReviewDecision;
  paths: string[];
  languages: string[];
};

export type SurveySourceReviewItem = {
  path: string;
  targetType: SurveySourceReviewTargetType;
  sourceReference: string;
  status: SurveySourceReviewStatus;
  reviewedByMemberId?: string;
  reviewedAt?: string;
};

export type SurveySourceReviewQueue = {
  items: SurveySourceReviewItem[];
};

export type EnhancedSurveyParentCreateRequest = {
  title: string;
  description: string;
  dateStart: string;
  dateEnd: string | null;
  questions: [];
};

export type EnhancedSurveyParent = {
  id: string;
  title?: string;
  description?: string;
  dateStart?: string;
  dateEnd?: string | null;
};

export type GovernedSurveyDuplicateCommand = {
  title: string;
};

export type GovernedSurveyMetadataUpdate = {
  title: string;
};

export type GovernedSurveyMetadata = {
  surveyId: string;
  title: string;
};

export type SurveyLanguageApprovalCommand = {
  concurrencyToken: string;
  language: string;
  status: 'approved' | 'rejected';
};

export type SurveyTranslationDraftCommand = {
  concurrencyToken: string;
  targetLanguage: string;
};

export type SurveyVersionCopyCommand = {
  newVersion: string;
};

export type SurveyLifecycleAction =
  | 'submit'
  | 'reopen'
  | 'approve'
  | 'publish'
  | 'supersede'
  | 'retire';

export type SurveyLifecycleCommand = {
  concurrencyToken: string;
  approvalVersion?: string;
  currentPublishedVersion?: string;
  currentPublishedConcurrencyToken?: string;
};
